/**
 * Shared master-label maps for Hired / Rented Equipment screens.
 * Loads suppliers, PMV profiles, projects, and optional PO numbers for human display.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import pmvService from '@/services/pmv.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { useProjects } from '@/hooks/useProjects';
import { formatProjectOption, buildProjectIdToLabelMap } from '@/utils/pmvDisplay';
import {
  formatHireEquipmentLabel,
  unwrapApiList,
} from '@/utils/pmvHireUi';

export function useHireMasterLabels(opts?: { enablePos?: boolean }) {
  const [supplierSearch, setSupplierSearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const { data: projects = [] } = useProjects();

  const suppliersQ = useQuery({
    queryKey: ['pmv-hire-suppliers', supplierSearch],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers', {
        params: supplierSearch ? { search: supplierSearch } : undefined,
      });
      return unwrapApiList<{
        id: string;
        supplier_code: string;
        supplier_name: string;
        is_active?: boolean;
      }>(res);
    },
    staleTime: 30_000,
  });

  const profilesQ = useQuery({
    queryKey: ['pmv-hire-profiles-labels', equipmentSearch],
    queryFn: () =>
      pmvService.listProfiles({
        page: 1,
        pageSize: 200,
        q: equipmentSearch || undefined,
      }),
    staleTime: 30_000,
  });

  const posQ = useQuery({
    queryKey: ['pmv-hire-po-labels'],
    queryFn: () => purchaseOrderService.list({}),
    enabled: !!opts?.enablePos,
    staleTime: 60_000,
  });

  const supplierLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of suppliersQ.data ?? []) {
      m.set(s.id, `${s.supplier_code} — ${s.supplier_name}`);
    }
    return m;
  }, [suppliersQ.data]);

  const equipmentLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profilesQ.data?.data ?? []) {
      m.set(p.id, formatHireEquipmentLabel(p));
    }
    return m;
  }, [profilesQ.data]);

  const projectLabelById = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const poLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const po of posQ.data ?? []) {
      m.set(po.id, `${po.po_number}${po.po_status ? ` · ${po.po_status}` : ''}`);
    }
    return m;
  }, [posQ.data]);

  const supplierOptions = useMemo(
    () =>
      (suppliersQ.data ?? [])
        .filter((s) => s.is_active !== false)
        .map((s) => ({
          value: s.id,
          label: `${s.supplier_code} — ${s.supplier_name}`,
        })),
    [suppliersQ.data]
  );

  const equipmentOptions = useMemo(() => {
    const rows = profilesQ.data?.data ?? [];
    return rows.map((p) => ({ value: p.id, label: formatHireEquipmentLabel(p) }));
  }, [profilesQ.data]);

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: formatProjectOption(p) })),
    [projects]
  );

  return {
    supplierSearch,
    setSupplierSearch,
    equipmentSearch,
    setEquipmentSearch,
    supplierLabelById,
    equipmentLabelById,
    projectLabelById,
    poLabelById,
    supplierOptions,
    equipmentOptions,
    projectOptions,
    suppliersLoading: suppliersQ.isLoading,
    profilesLoading: profilesQ.isLoading,
  };
}
